import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { formatDate } from "../utils/format";
import { toast } from "sonner";
import { User, Key, Bell, CheckSquare, Activity, Save } from "lucide-react";

export default function Profile() {
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", notifications: { email: true, in_app: true } });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });

  const load = async () => {
    const { data } = await api.get("/profile/me");
    setMe(data);
    setForm({
      name: data.name || "",
      phone: data.phone || "",
      notifications: data.notifications || { email: true, in_app: true },
    });
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    try {
      await api.patch("/profile/me", form);
      toast.success("Profile updated");
      setEditing(false);
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const changePassword = async () => {
    if (pw.new_password !== pw.confirm) return toast.error("New passwords do not match");
    if (pw.new_password.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.post("/profile/me/change-password", {
        current_password: pw.current_password, new_password: pw.new_password,
      });
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Password change failed"); }
  };

  if (!me) return <Layout title="Loading…"><div className="text-sm text-muted-foreground p-6">Loading profile…</div></Layout>;

  return (
    <Layout title="My Profile" subtitle={me.email}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3"><User className="w-4 h-4 text-muted-foreground" /><div className="label-mono">Identity</div></div>
            <div className="space-y-3">
              <Field label="Email"><div className="text-sm font-mono">{me.email}</div></Field>
              <Field label="Role"><StatusBadge tone={me.role === "super_admin" ? "critical" : me.role === "admin" ? "primary" : "muted"}>{me.role.replace("_", " ")}</StatusBadge></Field>
              <Field label="Name">
                {editing
                  ? <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-9 px-2 border border-border rounded-sm text-sm" data-testid="profile-name-input" />
                  : <div className="text-sm">{me.name}</div>}
              </Field>
              <Field label="Phone">
                {editing
                  ? <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full h-9 px-2 border border-border rounded-sm text-sm" />
                  : <div className="text-sm">{me.phone || "—"}</div>}
              </Field>
            </div>
            <div className="mt-4 pt-4 border-t border-border flex gap-2">
              {editing ? (
                <>
                  <button onClick={saveProfile} data-testid="profile-save-btn" className="flex-1 h-9 bg-primary text-primary-foreground text-xs font-medium rounded-sm flex items-center justify-center gap-1.5"><Save className="w-3.5 h-3.5" /> Save</button>
                  <button onClick={() => setEditing(false)} className="flex-1 h-9 border border-border text-xs rounded-sm">Cancel</button>
                </>
              ) : (
                <button onClick={() => setEditing(true)} data-testid="profile-edit-btn" className="w-full h-9 border border-border text-xs rounded-sm hover:bg-secondary">Edit Profile</button>
              )}
            </div>
          </div>

          <div className="bg-card border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3"><Bell className="w-4 h-4 text-muted-foreground" /><div className="label-mono">Notifications</div></div>
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" disabled={!editing} checked={form.notifications?.email || false} onChange={(e) => setForm({ ...form, notifications: { ...form.notifications, email: e.target.checked } })} /> Email alerts</label>
              <label className="flex items-center gap-2"><input type="checkbox" disabled={!editing} checked={form.notifications?.in_app || false} onChange={(e) => setForm({ ...form, notifications: { ...form.notifications, in_app: e.target.checked } })} /> In-app notifications</label>
            </div>
          </div>

          <div className="bg-card border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3"><Key className="w-4 h-4 text-muted-foreground" /><div className="label-mono">Change Password</div></div>
            <div className="space-y-2">
              <input type="password" placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="w-full h-9 px-3 border border-border rounded-sm text-sm" data-testid="pw-current" />
              <input type="password" placeholder="New password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="w-full h-9 px-3 border border-border rounded-sm text-sm" data-testid="pw-new" />
              <input type="password" placeholder="Confirm new" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="w-full h-9 px-3 border border-border rounded-sm text-sm" data-testid="pw-confirm" />
              <button onClick={changePassword} data-testid="pw-change-btn" className="w-full h-9 bg-primary text-primary-foreground text-xs font-medium rounded-sm mt-1">Update Password</button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="label-mono mb-3">Assigned Clients ({me.assigned_clients?.length || 0})</div>
            <div className="flex flex-wrap gap-2">
              {me.assigned_clients?.map((c) => <a key={c.id} href={`/clients/${c.id}`} className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-secondary">{c.name}</a>)}
              {!me.assigned_clients?.length && <div className="text-xs text-muted-foreground">No clients assigned.</div>}
            </div>
          </div>
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="label-mono mb-3">Assigned Accounts ({me.assigned_accounts?.length || 0})</div>
            <div className="flex flex-wrap gap-2">
              {me.assigned_accounts?.map((a) => <a key={a.id} href={`/accounts/${a.id}`} className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-secondary">{a.name} <span className="text-muted-foreground font-mono">· {a.google_customer_id}</span></a>)}
              {!me.assigned_accounts?.length && <div className="text-xs text-muted-foreground">No accounts assigned.</div>}
            </div>
          </div>
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3"><CheckSquare className="w-4 h-4 text-muted-foreground" /><div className="label-mono">Open Tasks Assigned to Me ({me.open_tasks?.length || 0})</div></div>
            <div className="space-y-2">
              {me.open_tasks?.map((t) => (
                <div key={t.id} className="flex justify-between py-1.5 border-b border-border last:border-b-0">
                  <span className="text-sm font-medium">{t.title}</span>
                  <span className="text-xs text-muted-foreground">{t.due_date ? formatDate(t.due_date) : "no due"} · {t.priority}</span>
                </div>
              ))}
              {!me.open_tasks?.length && <div className="text-xs text-muted-foreground">No open tasks.</div>}
            </div>
          </div>
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3"><Activity className="w-4 h-4 text-muted-foreground" /><div className="label-mono">Recent Activity</div></div>
            <div className="space-y-1.5 text-xs">
              {me.recent_activity?.slice(0, 15).map((a, i) => (
                <div key={i} className="flex justify-between py-1 border-b border-border last:border-b-0">
                  <span>{a.action} · <span className="font-mono text-muted-foreground">{a.entity_type}</span></span>
                  <span className="text-muted-foreground">{formatDate(a.created_at)}</span>
                </div>
              ))}
              {!me.recent_activity?.length && <div className="text-xs text-muted-foreground">No recent activity logged.</div>}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      {children}
    </div>
  );
}
